import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import ConstructionIcon from '@mui/icons-material/Construction';
import { FilterChip } from '../FilterChip/FilterChip';

interface LabelFilterProps {
  availableLabels: string[];
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
}

export const LabelFilter = ({
  availableLabels,
  selectedLabels,
  onLabelsChange,
}: Readonly<LabelFilterProps>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [labelSearch, setLabelSearch] = useState('');

  const toggle = (label: string) =>
    onLabelsChange(
      selectedLabels.includes(label)
        ? selectedLabels.filter(l => l !== label)
        : [...selectedLabels, label],
    );

  const filteredLabels = useMemo(
    () =>
      availableLabels.filter(l =>
        l.toLowerCase().includes(labelSearch.toLowerCase()),
      ),
    [availableLabels, labelSearch],
  );

  return (
    <>
      <FilterChip
        label="Tools"
        icon={<ConstructionIcon sx={{ fontSize: 18 }} />}
        active={selectedLabels.length > 0}
        tooltip="Filter by tool"
        onClick={e => setAnchor(e.currentTarget)}
        width={140}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setLabelSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { borderRadius: '15px' },
          },
        }}
      >
        <Box sx={{ pt: 1.5, pb: 0.5, minWidth: 200 }}>
          <Box sx={{ px: 1.5, pb: 1 }}>
            <OutlinedInput
              value={labelSearch}
              onChange={e => setLabelSearch(e.target.value)}
              placeholder="Search tools"
              size="small"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              sx={{ width: '100%' }}
            />
          </Box>
          {filteredLabels.length === 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {availableLabels.length === 0
                  ? 'No tools available'
                  : 'No matching tools'}
              </Typography>
            </Box>
          ) : (
            filteredLabels.map(label => {
              const isSelected = selectedLabels.includes(label);
              return (
                <MenuItem
                  key={label}
                  disableRipple
                  onClick={() => toggle(label)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    gap: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 120, 214, 0.08)',
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => toggle(label)}
                    onClick={e => e.stopPropagation()}
                    sx={{
                      p: 0,
                      color: 'text.secondary',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                  {label}
                </MenuItem>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};
